import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import Button from "../button";
import CustomImage from "../image";
import { LayoutContext } from "../layout-context-container";

function Tooltip({
  customClass = undefined,
}, ref) {
  const { isMobile } = useContext(LayoutContext);
  const containerRef = useRef(null);
  const [target, _setTarget] = useState(null);
  const targetRef = useRef(target);
  function setTarget(val) {
    _setTarget(val);
    targetRef.current = val;
  }
  const [stateOpen, _setStateOpen] = useState(false);
  const stateOpenRef = useRef(stateOpen);
  function setStateOpen(val) {
    _setStateOpen(val);
    stateOpenRef.current = val;
  }
  const [mediaMatches, _setMediaMatches] = useState(false);
  const mediaMatchesRef = useRef(stateOpen);
  function setMediaMatches(val) {
    _setMediaMatches(val);
    mediaMatchesRef.current = val;
  }
  
  const [heading, setHeading] = useState(null);
  const [content, setContent] = useState(null);
  const [openClass, setOpenClass] = useState(null);
  const offsetXRef = useRef(0);
  const offsetYRef = useRef(0);
  const positionFnRef = useRef(null);
  const containerClass = useMemo(() => {
    let className = 'tooltip';

    if (stateOpen) {
      className = `${className} tooltip--open`;
    }

    if (customClass) {
      className = `${className} ${customClass}`;
    }

    if (openClass) {
      className = `${className} ${openClass}`;
    }

    return className;
  }, [stateOpen, customClass, openClass]);

  function getPageCoordinates(element) {
    const rect = element.getBoundingClientRect();
    const pageX = rect.left + window.scrollX;
    const pageY = rect.top + window.scrollY;
  
    return { x: pageX, y: pageY, width: rect.width, height: rect.height };
  }

  function position() {
    if (positionFnRef.current) {
      const result = positionFnRef.current(targetRef.current, containerRef.current);
      if (result) {
        containerRef.current?.style?.setProperty('top', `${result.top}px`);
        containerRef.current?.style?.setProperty('left', `${result.left}px`);
        return;
      }
    }

    const {x, y, width, height} = getPageCoordinates(targetRef.current);
    const rect = containerRef.current?.getBoundingClientRect();
    const currentMaxWidth = rect.width;
    const top = y + offsetYRef.current - 24 - (height / 2) - (rect.height / 2);
    let left = x + offsetXRef.current + ((width - currentMaxWidth) / 2);

    // check if it overflows
    if (left + currentMaxWidth + 40 > window.innerWidth) {
      left = window.innerWidth - currentMaxWidth - 40;
    }

    containerRef.current?.style?.setProperty('top', `${top}px`);
    containerRef.current?.style?.setProperty('left',`${left}px`);
  }

  useEffect(() => {
    function handleResize() {
      // reposition tooltip is window is resized
      if (targetRef.current) {
        position();

        if (stateOpenRef.current && mediaMatchesRef.current) {
          close();
        }
      }
    }

    function handleMedia(event) {
      // console.log(event.matches);
      setMediaMatches(event.matches);
    }

    const mediaQuery = window.matchMedia(`(max-width: 1024px)`);
    
    // Initial check
    setMediaMatches(mediaQuery.matches);
  
    // Listen for changes
    mediaQuery.addEventListener('change', handleMedia);
    window.addEventListener('resize', handleResize);

    return () => {
      mediaQuery.addEventListener('change', handleMedia);
      window.removeEventListener('resize', handleResize);
      
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    const lastTarget = targetRef.current;

    setStateOpen(false);
    setHeading(null);
    setContent(null);
    setOpenClass(null);
    setTarget(null);
    positionFnRef.current = null;

    lastTarget?.focus();
  }

  useEffect(() => {
    const tooltip = containerRef.current;
    let firstFocusableElement;
    let lastFocusableElement;
    
    if (tooltip) {
      const focusableElements = tooltip?.querySelectorAll('input, a[href], button, [tabindex]:not([tabindex="-1"])');
      firstFocusableElement = focusableElements[0];
      lastFocusableElement = focusableElements[focusableElements.length - 1];
    }

    function handeKeyDown(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            e.preventDefault();
            lastFocusableElement?.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            firstFocusableElement?.focus();
          }
        }
      }
    }

    function handleClick(event) {
      if (!target?.contains(event.target) && !containerRef.current?.contains(event.target)) {
         setStateOpen(false);
      }
    }

    if (stateOpen) {
      tooltip?.addEventListener('keydown', handeKeyDown);
      document.addEventListener('click', handleClick);
      
      setTimeout(() => firstFocusableElement?.focus(), 333);

      position();
    }

    return () => {
      document.removeEventListener('click', handleClick);
      tooltip?.addEventListener('keydown', handeKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOpen]);

  useImperativeHandle(
    ref,
    () => ({
      open({ heading = null, content = null, maxWidth = undefined, offsetX = 0, offsetY = 0, target = undefined, openClass = null, positionFn = null }) {
        if (!target) {
          return;
        }

        if (stateOpenRef.current) {
          close();
          return;
        }

        setTarget(target);
        
        if (maxWidth) {
          containerRef.current?.style?.setProperty('--max-width', maxWidth);
        } else {
          containerRef.current?.style?.removeProperty('--max-width');
        }

        setHeading(heading);
        setContent(content);
        setOpenClass(openClass);
        positionFnRef.current = positionFn;
        setStateOpen(true);
        offsetXRef.current = isMobile ? 0 : offsetX;
        offsetYRef.current = isMobile ? 0 : offsetY;
      },
      close() {
        close();
      }
    })
  )

  return (
    <div className={containerClass} ref={containerRef} role="tooltip" aria-label={renderToString(heading)}>
      <Button
        customClass="tooltip__close"
        type="button"
        onClick={() => ref.current?.close()}
      >
        CLOSE
        <CustomImage
          src="/images/icons/tooltip/Close-Icon.svg"
          width={20}
          height={20}
          alt="Click to close"
        />
      </Button>
      {heading && <div className="tooltip__heading">
        {heading}
      </div>}
      {content && <div className="tooltip__content">
        {content}
      </div>}
    </div>
  );
}

export default forwardRef(Tooltip);